import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { View, ViewSchema } from "@common/types/views/views";
import {
    useEliminationDetailReportQuery,
    useEliminationExceptionsReportQuery,
} from "@api/accountingReports.api";
import { pluralize } from "@common/helpers/string";
import { Card } from "@design-system/Card/Card";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { useTransactionsByIdsQuery } from "@components/transactions/useTransactionsByIdsQuery";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useEntitiesById } from "@hooks/useEntities";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import {
    useOrganizationUserCache,
    ORGANIZATION_USER_CACHE_KEYS,
} from "@hooks/useOrganizationUserCache";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { mergeCachedView } from "@lib/mergeCachedView";
import { useOverlays } from "contexts/OverlayContext";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { defaultGlTransactionsDrilldown } from "../generalLedger/glTransactionsDrilldown";
import { useAccountingTabContext } from "../useAccountingTab";
import { useConsolidatedReportView } from "../useConsolidatedReportView";
import { IntercompanyTransactionsFilters } from "./IntercompanyTransactionsFilters";
import {
    buildIntercompanyTransactionRows,
    filterAndSortIntercompanyTransactionRows,
    IntercompanyTransactionRow,
    parseIntercompanyAccountCodes,
    parseIntercompanyEntityIds,
    parseIntercompanySources,
    parseIntercompanyStatuses,
} from "./intercompanyTransactions.lib";
import { applyIntercompanySearchParamUpdates } from "./intercompanyTransactionsSearchParams.lib";
import {
    getIntercompanyReportLoadGate,
    getIntercompanyTransactionsPageLoadState,
    toHydrationQueryLoadFlags,
} from "./intercompanyTransactionsPageLoad.lib";
import { getComputedMatchContext } from "./intercompanyMatchPresentation";
import {
    defaultIntercompanyTransactionsView,
    IntercompanyTransactionsTableMeta,
    intercompanyTransactionsViewConfig,
} from "./intercompanyTransactionsViewConfig";
import styles from "./IntercompanyTransactionsTable.module.scss";

export const IntercompanyTransactionsPreviewPage = (): React.ReactElement => {
    const { filters, setFilters, ledgerBasis } = useAccountingTabContext();
    const { workspaceId } = useScope();
    const { consolidationGroup, parentEntity, isResolving } =
        useConsolidatedReportView({
            filters,
            setFilters,
        });
    const navigate = useStateAwareNavigate();
    const { openOverlay } = useOverlays();
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const isClassesEnabled = useIsClassesEnabled();
    const entitiesById = useEntitiesById();
    const [searchParams, setSearchParams] = useSearchParams();
    const [sort, setSort] = useState<"date_asc" | "date_desc">("date_desc");
    const [cachedView, setCachedView] = useOrganizationUserCache<View>(
        ORGANIZATION_USER_CACHE_KEYS.INTERCOMPANY_TRANSACTIONS_PAGE_VIEW_CACHE_KEY,
        ViewSchema,
    );
    const [view, setView] = useState<View>(() =>
        mergeCachedView(
            cachedView,
            defaultIntercompanyTransactionsView,
            intercompanyTransactionsViewConfig,
        ),
    );
    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_INTERCOMPANY_TRANSACTIONS_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const updateFilterValues = useCallback(
        (
            updates: ReadonlyArray<{ key: string; values: readonly string[] }>,
        ) => {
            setSearchParams((current) =>
                applyIntercompanySearchParamUpdates(current, updates),
            );
        },
        [setSearchParams],
    );
    const selectedStatuses = parseIntercompanyStatuses(
        searchParams.getAll("intercompanyStatus"),
    );
    const selectedSources = parseIntercompanySources(
        searchParams.getAll("intercompanySource"),
    );
    const selectedEntityIds = parseIntercompanyEntityIds(
        searchParams.getAll("intercompanyEntityId"),
    );
    const selectedAccountIds = searchParams.getAll("intercompanyAccountId");
    const selectedAccountCodes = parseIntercompanyAccountCodes(
        searchParams.getAll("accountCode"),
    );
    const reportQuery =
        consolidationGroup &&
        filters.consolidationGroupId === consolidationGroup.id
            ? {
                  workspaceId: filters.workspaceId,
                  consolidationGroupId: consolidationGroup.id,
                  startDate: filters.startDate,
                  endDate: filters.endDate,
              }
            : undefined;
    const detailReport = useEliminationDetailReportQuery(reportQuery);
    const exceptionsReport = useEliminationExceptionsReportQuery(reportQuery);
    const rows = useMemo(
        () =>
            buildIntercompanyTransactionRows({
                detailRows: detailReport.data?.rows ?? [],
                exceptionRows: exceptionsReport.data?.rows ?? [],
            }),
        [detailReport.data?.rows, exceptionsReport.data?.rows],
    );
    const transactionIds = useMemo(
        () => [
            ...new Set(
                rows.flatMap((row) =>
                    row.sourceTransactionId === undefined
                        ? []
                        : [row.sourceTransactionId],
                ),
            ),
        ],
        [rows],
    );
    const transactionEntityIds = useMemo(
        () => [
            ...new Set(
                rows
                    .filter((row) => row.sourceTransactionId !== undefined)
                    .map((row) => row.entityId),
            ),
        ],
        [rows],
    );
    const isHydrationRequired =
        transactionIds.length > 0 && transactionEntityIds.length > 0;
    const transactionsQuery = useTransactionsByIdsQuery({
        transactionIds,
        entityIds: transactionEntityIds,
        includeMatchDetails: true,
        enabled: isHydrationRequired,
    });
    const hydratedRows = useMemo(() => {
        const transactionsById = new Map(
            transactionsQuery.data.map((transaction) => [
                transaction.id,
                transaction,
            ]),
        );
        return rows.map((row) => {
            const transaction =
                row.sourceTransactionId === undefined
                    ? undefined
                    : transactionsById.get(row.sourceTransactionId);
            return transaction?.entity.id === row.entityId
                ? { ...row, transaction }
                : row;
        });
    }, [rows, transactionsQuery.data]);
    const accounts = useMemo(
        () =>
            [
                ...new Map(
                    hydratedRows
                        .flatMap((row) => row.counterpartAccounts)
                        .map((account) => [account.id, account]),
                ).values(),
            ].sort((firstAccount, secondAccount) =>
                firstAccount.label.localeCompare(secondAccount.label),
            ),
        [hydratedRows],
    );
    const visibleRows = useMemo(
        () =>
            filterAndSortIntercompanyTransactionRows({
                rows: hydratedRows,
                search: filters.search,
                statuses: selectedStatuses,
                entityIds: selectedEntityIds,
                accountIds: selectedAccountIds,
                accountCodes: selectedAccountCodes,
                sources: selectedSources,
                sort,
            }),
        [
            hydratedRows,
            filters.search,
            selectedStatuses,
            selectedEntityIds,
            selectedAccountIds,
            selectedAccountCodes,
            selectedSources,
            sort,
        ],
    );

    useEffect(() => {
        if (filters.consolidationGroupId || !consolidationGroup) {
            return;
        }
        setFilters((current) => ({
            ...current,
            consolidationGroupId: consolidationGroup.id,
            entityId: current.entityId ?? parentEntity?.id,
            ledgerId:
                current.ledgerId ??
                consolidationGroup.overlayLedgerId ??
                undefined,
        }));
    }, [
        filters.consolidationGroupId,
        consolidationGroup,
        parentEntity,
        setFilters,
    ]);

    const handleCreateJournalEntry = useCallback(
        (row: IntercompanyTransactionRow) => {
            const entity = entitiesById.get(
                getComputedMatchContext(row).entityId,
            );
            if (!entity) {
                return;
            }
            openOverlay("journalEntryDrawer", {
                entity,
                initialDate: row.date ?? undefined,
            });
        },
        [entitiesById, openOverlay],
    );
    const handleFindTransactionMatch = useCallback(
        (row: IntercompanyTransactionRow) => {
            if (row.sourceTransactionId === undefined) {
                return;
            }
            openOverlay("transactionMatch", {
                transactionId: row.sourceTransactionId,
            });
        },
        [openOverlay],
    );
    const handleOpenJournalEntry = useCallback(
        (row: IntercompanyTransactionRow) => {
            const [journalEntry] = row.journalEntries;
            const entity = entitiesById.get(row.entityId);
            if (!journalEntry || !entity) {
                return;
            }
            openOverlay("journalEntryDrawer", {
                entity,
                journalEntryId: journalEntry.referenceNumber,
                ledgerId: journalEntry.accountingLedgerId,
            });
        },
        [entitiesById, openOverlay],
    );
    const handleOpenMatch = useCallback(
        (row: IntercompanyTransactionRow) => {
            const match = row.matchContext;
            if (match?.accountingLedgerId && match.reference !== "") {
                const entity = entitiesById.get(match.entityId);
                if (entity) {
                    openOverlay("journalEntryDrawer", {
                        entity,
                        journalEntryId: match.reference,
                        ledgerId: match.accountingLedgerId,
                    });
                    return;
                }
            }
            handleOpenJournalEntry(row);
        },
        [entitiesById, handleOpenJournalEntry, openOverlay],
    );
    const handleOpenTransaction = useCallback(
        (row: IntercompanyTransactionRow) => {
            if (
                row.source !== "transaction" ||
                row.sourceTransactionId === undefined
            ) {
                return;
            }
            const resolvedLedgerBasis =
                ledgerBasis ?? AccountingLedgerBasis.cash;
            openGlobalTransactionViewDrawer(
                {
                    transactionIds: [row.sourceTransactionId],
                    entityIds: [row.entityId],
                },
                {
                    cacheKey:
                        defaultGlTransactionsDrilldown.getCacheKey(
                            resolvedLedgerBasis,
                        ),
                    baseViewOverride: defaultGlTransactionsDrilldown.createView(
                        resolvedLedgerBasis,
                        { isClassesEnabled },
                    ),
                },
            );
        },
        [ledgerBasis, openGlobalTransactionViewDrawer, isClassesEnabled],
    );
    const tableMeta = useMemo<IntercompanyTransactionsTableMeta>(
        () => ({
            callbacks: {
                onCreateJournalEntry: handleCreateJournalEntry,
                onFindTransactionMatch: handleFindTransactionMatch,
                onOpenJournalEntry: handleOpenJournalEntry,
                onOpenMatch: handleOpenMatch,
                onOpenTransaction: handleOpenTransaction,
            },
        }),
        [
            handleCreateJournalEntry,
            handleFindTransactionMatch,
            handleOpenJournalEntry,
            handleOpenMatch,
            handleOpenTransaction,
        ],
    );
    const handleViewChange = useCallback(
        (nextView: View) => {
            setView(nextView);
            setCachedView(nextView);
        },
        [setCachedView],
    );
    const pageLoad = getIntercompanyTransactionsPageLoadState({
        ...getIntercompanyReportLoadGate({
            scopeWorkspaceId: workspaceId,
            reportQuery,
        }),
        isResolvingGroup: isResolving,
        hasConsolidationGroup: Boolean(consolidationGroup),
        detail: detailReport,
        exceptions: exceptionsReport,
        hydration: toHydrationQueryLoadFlags({
            isHydrationRequired,
            isLoading: transactionsQuery.isLoading,
            isError: transactionsQuery.isError,
            isSuccess: transactionsQuery.isSuccess,
        }),
    });
    const reportBody = (
        <Card stacked sharpEdges fullPage className={styles.report}>
            <ViewTable
                data={visibleRows}
                view={view}
                config={intercompanyTransactionsViewConfig}
                className={styles.intercompanyTransactionsTable}
                emptyView={() => (
                    <EmptyState header="No intercompany transactions for this period" />
                )}
                enableColumnResizing
                fixedTopOffsetPx
                onViewChange={handleViewChange}
                tableMeta={tableMeta}
                sort={sort}
                onSortChange={setSort}
                footer={
                    <CommandIsland
                        sticky
                        className={commandIslandStyles.bulkActionsStickyFooter}
                    >
                        <CommandIslandTotal>
                            {pluralize("Entry", visibleRows.length)}:{" "}
                            {visibleRows.length}
                        </CommandIslandTotal>
                    </CommandIsland>
                }
            />
        </Card>
    );

    const reportContent = (() => {
        if (pageLoad.shouldShowError) {
            return (
                <EmptyState
                    header="Couldn't load report"
                    body="Something went wrong loading intercompany transactions. Please try again."
                />
            );
        }
        if (pageLoad.shouldShowMissingGroup) {
            return (
                <EmptyState
                    header="Create a consolidation group"
                    body="Create a consolidation group to view and match intercompany transactions."
                    ctaText="Go to Ownership"
                    action={() => navigate("/ownership")}
                />
            );
        }
        if (pageLoad.shouldShowLoader) {
            return <Loader />;
        }
        return (
            <LoaderOnInitialLoading
                queryResult={{
                    isLoading: false,
                    isSuccess: pageLoad.shouldShowReport,
                    isPlaceholderData: pageLoad.shouldShowStaleOverlay,
                    data: detailReport.data ?? exceptionsReport.data,
                }}
                dimWhileFetching
            >
                {reportBody}
            </LoaderOnInitialLoading>
        );
    })();

    return (
        <>
            <AccountingReportFiltersForm
                filters={filters}
                setFilters={setFilters}
                consolidationGroup={consolidationGroup}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                requireDateSelection
                hideClassFilter
                hideEntityFilter
                hideLedgerSelect
                hideCreateJournalEntryButton
                entityFilterOverride={
                    <EntityFilterCombobox
                        filters={{ entityIds: selectedEntityIds }}
                        onlyBusiness
                        excludeAllMockEntities
                        onChange={(nextFilters) =>
                            updateFilterValues([
                                {
                                    key: "intercompanyEntityId",
                                    values: (nextFilters.entityIds ?? []).map(
                                        String,
                                    ),
                                },
                            ])
                        }
                    />
                }
                beforeFiltersChildren={
                    <IntercompanyTransactionsFilters
                        statuses={selectedStatuses}
                        accountIds={selectedAccountIds}
                        sources={selectedSources}
                        accounts={accounts}
                        onStatusesChange={(statuses) =>
                            updateFilterValues([
                                { key: "intercompanyStatus", values: statuses },
                            ])
                        }
                        onAccountIdsChange={(accountIds) => {
                            updateFilterValues([
                                {
                                    key: "intercompanyAccountId",
                                    values: accountIds,
                                },
                                { key: "accountCode", values: [] },
                            ]);
                        }}
                        onSourcesChange={(sources) =>
                            updateFilterValues([
                                { key: "intercompanySource", values: sources },
                            ])
                        }
                    />
                }
                additionalButtons={
                    <ColumnSettings
                        view={view}
                        onViewChange={handleViewChange}
                        viewConfig={intercompanyTransactionsViewConfig}
                        defaultView={defaultIntercompanyTransactionsView}
                    />
                }
            />
            {reportContent}
        </>
    );
};
